/**
 * API service for warehouse location, navigation, and picking operations
 */

import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import { PaginatedResponse } from '../types/api';
import {
  NavigationGraph,
  RackDetail,
  PickingPath,
  ProductLocationsDetail,
  CalculatePickingPathRequest,
  CreateNavigationNodesRequest,
  CreateNavigationEdgesRequest,
  MapImportData,
} from '../components/WarehouseMap/pathTypes';

const apiBaseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

/* eslint-disable @typescript-eslint/no-explicit-any */
const api = {
  get: (url: string, config?: any) =>
    axios.get<any>(`${apiBaseUrl}${url}`, config),
  post: (url: string, data?: any, config?: any) =>
    axios.post<any>(`${apiBaseUrl}${url}`, data, config),
  put: (url: string, data?: any, config?: any) =>
    axios.put<any>(`${apiBaseUrl}${url}`, data, config),
  patch: (url: string, data?: any, config?: any) =>
    axios.patch<any>(`${apiBaseUrl}${url}`, data, config),
  delete: (url: string, config?: any) =>
    axios.delete<any>(`${apiBaseUrl}${url}`, config),
};
/* eslint-enable @typescript-eslint/no-explicit-any */

// ============================================
// Navigation Graph API
// ============================================

/**
 * Get the complete navigation graph for a warehouse
 */
export const getNavigationGraph = async (warehouseId: number): Promise<NavigationGraph> => {
  const response = await api.get(`/warehouses/${warehouseId}/navigation_graph/`);
  return {
    warehouseId: response.data.warehouse_id,
    warehouseName: response.data.warehouse_name,
    nodes: response.data.nodes.map((n: any) => ({
      id: n.id,
      code: n.code,
      name: n.name,
      nodeType: n.type,
      position: n.position,
      connectedLocationId: n.connectedLocationId,
      connectedLocationCode: n.connectedLocationCode,
    })),
    edges: response.data.edges.map((e: any) => ({
      id: e.id,
      fromNodeId: e.fromNodeId,
      toNodeId: e.toNodeId,
      fromNodeCode: e.fromNodeCode,
      toNodeCode: e.toNodeCode,
      distance: e.distance,
      isBidirectional: e.isBidirectional,
      edgeType: e.edgeType ?? 'BOTH',
    })),
  };
};

/**
 * Create or update navigation nodes
 */
export const updateNavigationNodes = async (
  warehouseId: number,
  request: CreateNavigationNodesRequest
): Promise<{ success: boolean; createdCount: number; updatedCount: number; errors?: string[] }> => {
  const response = await api.post(`/warehouses/${warehouseId}/navigation_nodes/`, {
    nodes: request.nodes.map((n) => ({
      id: n.id,
      code: n.code,
      name: n.name,
      node_type: n.nodeType,
      position_x: n.positionX,
      position_y: n.positionY,
      connected_location_id: n.connectedLocationId,
      is_active: n.isActive ?? true,
    })),
  });
  return {
    success: response.data.success,
    createdCount: response.data.created_count,
    updatedCount: response.data.updated_count,
    errors: response.data.errors,
  };
};

/**
 * Create navigation edges
 */
export const createNavigationEdges = async (
  warehouseId: number,
  request: CreateNavigationEdgesRequest
): Promise<{ success: boolean; createdCount: number; errors?: string[] }> => {
  const response = await api.post(`/warehouses/${warehouseId}/navigation_edges/`, {
    edges: request.edges.map((e) => ({
      from_node_id: e.fromNodeId,
      to_node_id: e.toNodeId,
      is_bidirectional: e.isBidirectional ?? true,
      edge_type: e.edgeType ?? 'BOTH',
      associated_aisle_id: e.associatedAisleId,
    })),
  });
  return {
    success: response.data.success,
    createdCount: response.data.created_count,
    errors: response.data.errors,
  };
};

/**
 * Update a navigation edge (e.g. edge_type, is_bidirectional)
 */
export const updateNavigationEdge = async (
  warehouseId: number,
  edgeId: number,
  data: { edgeType?: string; isBidirectional?: boolean; isActive?: boolean }
): Promise<any> => {
  const body: any = {};
  if (data.edgeType !== undefined) body.edge_type = data.edgeType;
  if (data.isBidirectional !== undefined) body.is_bidirectional = data.isBidirectional;
  if (data.isActive !== undefined) body.is_active = data.isActive;
  const response = await api.patch(`/warehouses/${warehouseId}/navigation_edges/${edgeId}/`, body);
  return response.data;
};

/**
 * Delete a navigation node
 */
export const deleteNavigationNode = async (
  warehouseId: number,
  nodeId: number
): Promise<{ success: boolean }> => {
  const response = await api.delete(`/warehouses/${warehouseId}/navigation_nodes/${nodeId}/`);
  return { success: response.data.success };
};

// ============================================
// Rack Detail API
// ============================================

/**
 * Get detailed rack view (cross-section) with all shelves and products
 */
export const getRackDetail = async (locationId: number): Promise<RackDetail> => {
  const response = await api.get(`/storage-locations/${locationId}/rack_detail/`);
  return {
    id: response.data.id,
    code: response.data.code,
    name: response.data.name,
    locationType: response.data.location_type,
    position: {
      x: response.data.position_x,
      y: response.data.position_y,
    },
    width: response.data.width,
    height: response.data.height,
    rotation: response.data.rotation,
    color: response.data.color,
    maxItems: response.data.max_items,
    shelves: response.data.shelves,
    accessNode: response.data.access_node,
  };
};

// ============================================
// Picking Path API
// ============================================

/**
 * Calculate optimal picking path for a document or list of items
 */
export const calculatePickingPath = async (
  warehouseId: number,
  request: CalculatePickingPathRequest
): Promise<PickingPath> => {
  const response = await api.post(`/warehouses/${warehouseId}/calculate_picking_path/`, {
    document_id: request.documentId,
    items: request.items?.map((i) => ({
      product_id: i.productId,
      quantity: i.quantity,
    })),
    strategy: request.strategy,
    start_node_id: request.startNodeId,
    transport_mode: request.transportMode ?? 'forklift',
  });
  
  return {
    steps: response.data.steps.map((s: any) => ({
      order: s.order,
      location: {
        id: s.location.id,
        code: s.location.code,
        name: s.location.name,
        type: s.location.type,
        position: s.location.position,
        shelfLevel: s.location.shelfLevel,
        shelfHeightCm: s.location.shelfHeightCm,
        slotIndex: s.location.slotIndex,
      },
      rack: s.rack ? {
        id: s.rack.id,
        code: s.rack.code,
        name: s.rack.name,
      } : undefined,
      product: s.product,
      quantityToPick: s.quantityToPick,
      availableQuantity: s.availableQuantity,
      batchNumber: s.batchNumber,
      expiryDate: s.expiryDate,
      heightAccessibility: s.heightAccessibility,
      heightPenalty: s.heightPenalty,
      navigationNodeId: s.navigationNodeId,
    })),
    strategy: response.data.strategy,
    totalDistance: response.data.totalDistance,
    totalHeightPenalties: response.data.totalHeightPenalties,
    estimatedTimeSeconds: response.data.estimatedTimeSeconds,
    pathCoordinates: response.data.pathCoordinates,
    isComplete: response.data.isComplete,
    unfulfilledItems: response.data.unfulfilledItems || [],
  };
};

// ============================================
// Product Locations API
// ============================================

/**
 * Get all locations where a product is stored in a warehouse
 */
export const getProductLocations = async (
  productId: number,
  warehouseId: number
): Promise<ProductLocationsDetail> => {
  const response = await api.get('/stock/product_locations/', {
    params: {
      product: productId,
      warehouse: warehouseId,
    },
  });
  
  return {
    productId: response.data.productId,
    warehouseId: response.data.warehouseId,
    warehouseName: response.data.warehouseName,
    totalQuantity: response.data.totalQuantity,
    totalAvailable: response.data.totalAvailable,
    totalReserved: response.data.totalReserved,
    locationsCount: response.data.locationsCount,
    locations: response.data.locations.map((loc: any) => ({
      stockId: loc.stockId,
      location: {
        id: loc.location.id,
        code: loc.location.code,
        name: loc.location.name,
        type: loc.location.type,
        position: loc.location.position,
        shelfLevel: loc.location.shelfLevel,
        shelfHeightCm: loc.location.shelfHeightCm,
        slotIndex: loc.location.slotIndex,
        heightAccessibility: loc.location.heightAccessibility,
      },
      rack: loc.rack ? {
        id: loc.rack.id,
        code: loc.rack.code,
        name: loc.rack.name,
      } : undefined,
      quantity: loc.quantity,
      availableQuantity: loc.availableQuantity,
      reservedQuantity: loc.reservedQuantity,
      batchNumber: loc.batchNumber,
      expiryDate: loc.expiryDate,
      stockedAt: loc.stockedAt,
    })),
  };
};

// ============================================
// Map Import API
// ============================================

/**
 * Import warehouse map from JSON data
 */
export const importWarehouseMap = async (
  warehouseId: number,
  mapData: MapImportData
): Promise<{ success: boolean; createdLocations: number; createdNodes: number; createdEdges: number }> => {
  const response = await api.post(`/warehouses/${warehouseId}/import_map/`, mapData);
  return {
    success: response.data.success,
    createdLocations: response.data.created_locations,
    createdNodes: response.data.created_nodes,
    createdEdges: response.data.created_edges,
  };
};

// ============================================
// Unplaced Locations API
// ============================================

export interface UnplacedLocation {
  id: number;
  code: string;
  name: string;
  location_type: string;
  status: string;
  warehouse: number;
}

export const getUnplacedLocations = async (
  warehouseId: number
): Promise<{ count: number; locations: UnplacedLocation[] }> => {
  const response = await api.get(`/warehouses/${warehouseId}/unplaced_locations/`);
  return response.data;
};

export const autoPlaceLocations = async (
  warehouseId: number
): Promise<{ success: boolean; placed_count: number }> => {
  const response = await api.post(`/warehouses/${warehouseId}/auto_place_locations/`);
  return response.data;
};

// ============================================
// Shelf / Slot CRUD API
// ============================================

export const createShelf = async (
  rackId: number,
  warehouseId: number,
  data: {
    shelfLevel: number;
    heightCm: number;
    slotsCount: number;
    heightAccessibility?: string;
  }
): Promise<any> => {
  const results = [];
  for (let s = 1; s <= data.slotsCount; s++) {
    const response = await api.post('/storage-locations/', {
      name: `Półka ${data.shelfLevel} Slot ${s}`,
      location_type: 'SLOT',
      warehouse_id: warehouseId,
      parent_id: rackId,
      shelf_level: data.shelfLevel,
      shelf_height_cm: data.heightCm,
      slot_index: s,
      rack_id: rackId,
    });
    results.push(response.data);
  }
  return results;
};

export const deleteShelf = async (shelfLocationIds: number[]): Promise<void> => {
  for (const id of shelfLocationIds) {
    await api.delete(`/storage-locations/${id}/`);
  }
};

export const addStockToLocation = async (
  productId: number,
  locationId: number,
  quantity: number,
  batchNumber?: string
): Promise<any> => {
  const response = await api.post('/stock/add/', {
    product_id: productId,
    storage_location_id: locationId,
    quantity,
    batch_number: batchNumber || '',
  });
  return response.data;
};

export const removeStockFromLocation = async (
  productId: number,
  locationId: number,
  quantity: number,
  batchNumber?: string
): Promise<any> => {
  const response = await api.post('/stock/remove/', {
    product_id: productId,
    storage_location_id: locationId,
    quantity,
    batch_number: batchNumber || '',
  });
  return response.data;
};

// ============================================
// Storage Locations
// ============================================

/**
 * Location types included in bulk label PDF when `location_ids` is omitted.
 * Must stay in sync with `print_labels` in the backend (default include_types).
 */
export const BULK_LABEL_INCLUDE_TYPES = ['SHELF'] as const;

export function filterStorageLocationsForBulkLabels<T extends { location_type: string }>(
  locations: T[],
  includeTypes: readonly string[] = BULK_LABEL_INCLUDE_TYPES
): T[] {
  const allowed = new Set(includeTypes.map((t) => t.toUpperCase()));
  return locations.filter((loc) => allowed.has((loc.location_type || '').toUpperCase()));
}

export interface StorageLocationOption {
  id: number;
  code: string;
  name: string;
  location_type: string;
  status: string;
  temp?: boolean;
  warehouse: number;
  rack_code?: string | null;
  full_location_code?: string;
  /** Parent rack location id (for shelves/slots on a rack) */
  rack?: number | null;
}

export interface GetStorageLocationsParams {
  page?: number;
  page_size?: number;
  search?: string;
  type?: string;
}

export const getStorageLocations = async (
  warehouseId: number
): Promise<StorageLocationOption[]> => {
  const response = await api.get(`/storage-locations/`, {
    params: { warehouse: warehouseId, status: 'ACTIVE', no_pagination: 1 },
  });
  return response.data.results || response.data;
};

export const getPaginatedStorageLocations = async (
  warehouseId: number,
  params?: GetStorageLocationsParams
): Promise<PaginatedResponse<StorageLocationOption>> => {
  const response = await api.get(`/storage-locations/`, {
    params: {
      warehouse: warehouseId,
      status: 'ACTIVE',
      page: params?.page,
      page_size: params?.page_size,
      search: params?.search,
      type: params?.type,
    },
  });
  return response.data;
};

export interface LocationSuggestion {
  location_id: number;
  location_code: string;
  location_name: string;
  rack_code: string | null;
  suggested_quantity: number;
  current_product_stock: number;
  remaining_capacity: number | null;
}

export interface SuggestLocationsResponse {
  suggestions: LocationSuggestion[];
  auto_suggest_enabled: boolean;
}

export const suggestLocations = async (
  productId: number,
  warehouseId: number,
  quantity: number
): Promise<SuggestLocationsResponse> => {
  const response = await api.post(`/storage-locations/suggest/`, {
    product_id: productId,
    warehouse_id: warehouseId,
    quantity,
  });
  return response.data;
};

/** Rekord magazynu z API (ustawienia administracyjne) */
export interface WarehouseAdminRow {
  id: number;
  name: string;
  city?: string;
  auto_suggest_locations: boolean;
}

export const getWarehousesAdminList = async (): Promise<WarehouseAdminRow[]> => {
  const response = await api.get(`/warehouses/`, { params: { page_size: 500 } });
  const raw = response.data.results ?? response.data;
  const list = Array.isArray(raw) ? raw : [];
  return list.map((w: Record<string, unknown>) => ({
    id: w.id as number,
    name: String(w.name ?? ''),
    city: w.city as string | undefined,
    auto_suggest_locations: Boolean(w.auto_suggest_locations),
  }));
};

export const patchWarehouseAutoSuggest = async (
  warehouseId: number,
  autoSuggestLocations: boolean
): Promise<void> => {
  await api.patch(`/warehouses/${warehouseId}/`, {
    auto_suggest_locations: autoSuggestLocations,
  });
};

async function parseErrorBlob(blob: Blob): Promise<string> {
  try {
    const text = await blob.text();
    const j = JSON.parse(text) as { detail?: string; error?: string; message?: string };
    return j.detail || j.error || j.message || text || 'Błąd serwera';
  } catch {
    return 'Błąd serwera';
  }
}

/**
 * Open a PDF blob URL in a tab opened synchronously (pass the return value of
 * window.open('', '_blank') from the same click handler before any await).
 * If that tab could not be opened, falls back to a new-tab link click.
 */
export const openPdfBlobInBrowser = (previewWindow: Window | null, blobUrl: string): void => {
  const revokeLater = () => setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.replace(blobUrl);
    revokeLater();
    return;
  }
  const a = document.createElement('a');
  a.href = blobUrl;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  revokeLater();
};

/**
 * Generate PDF with QR-code labels for storage locations.
 * Returns a Blob URL. Use default axios instance so auth refresh interceptors apply.
 * Call openPdfBlobInBrowser(window.open('', '_blank'), url) from the same UI handler
 * that starts the request so the browser does not block the new tab.
 */
export const printLocationLabels = async (
  warehouseId: number,
  options?: { locationIds?: number[]; includeTypes?: string[] }
): Promise<string> => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/storage-locations/print_labels/`,
      {
        warehouse_id: warehouseId,
        location_ids: options?.locationIds,
        include_types: options?.includeTypes?.length
          ? [...options.includeTypes]
          : [...BULK_LABEL_INCLUDE_TYPES],
      },
      {
        responseType: 'blob',
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const blob: Blob = response.data;
    const ct = String(response.headers['content-type'] || '').toLowerCase();
    if (ct.includes('application/json')) {
      throw new Error(await parseErrorBlob(blob));
    }

    const buf = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
    const isPdf =
      buf.length >= 4 &&
      buf[0] === 0x25 &&
      buf[1] === 0x50 &&
      buf[2] === 0x44 &&
      buf[3] === 0x46;

    if (!isPdf) {
      const head = new TextDecoder().decode(buf);
      if (head.startsWith('{') || head.startsWith('[')) {
        throw new Error(await parseErrorBlob(blob));
      }
      throw new Error('Serwer nie zwrócił prawidłowego pliku PDF');
    }

    const pdfBlob =
      blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
    return URL.createObjectURL(pdfBlob);
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
      throw new Error(await parseErrorBlob(err.response.data));
    }
    throw err;
  }
};

/**
 * Resolve a QR code value scanned from a location label.
 * QR format: LOC:{id}:{code}
 */
export interface ResolvedLocation {
  id: number;
  code: string;
  name: string;
  locationType: string;
  warehouseId: number;
  rackCode: string | null;
  shelfLevel: number;
  slotIndex: number;
  fullLocationCode: string;
}

export const resolveLocationQR = async (qrValue: string): Promise<ResolvedLocation> => {
  const response = await api.get('/storage-locations/resolve_qr/', {
    params: { qr: qrValue },
  });
  const d = response.data;
  return {
    id: d.id,
    code: d.code,
    name: d.name,
    locationType: d.location_type,
    warehouseId: d.warehouse_id,
    rackCode: d.rack_code,
    shelfLevel: d.shelf_level,
    slotIndex: d.slot_index,
    fullLocationCode: d.full_location_code,
  };
};

const locationApi = {
  getNavigationGraph,
  updateNavigationNodes,
  createNavigationEdges,
  deleteNavigationNode,
  getRackDetail,
  calculatePickingPath,
  getProductLocations,
  getStorageLocations,
  getPaginatedStorageLocations,
  importWarehouseMap,
  getUnplacedLocations,
  autoPlaceLocations,
  createShelf,
  deleteShelf,
  addStockToLocation,
  removeStockFromLocation,
  printLocationLabels,
  openPdfBlobInBrowser,
  resolveLocationQR,
  BULK_LABEL_INCLUDE_TYPES,
  filterStorageLocationsForBulkLabels,
};

export default locationApi;
